import type { PlaybookNodeSampleToolResultSectionSideAggregates } from "@repo/app-types/entities/playbookNodeSampleToolResult";
import { Size } from "@repo/ui/helpers/helpers";
import { Label as UiLabel } from "@repo/ui/Label/label";
import type { FC } from "react";
import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Label as ChartLabel,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from "recharts";

const CATEGORY_KEYS = [
  { key: "applicable", label: "Applicable" },
  { key: "not_applicable", label: "N/A" },
] as const;

const BASELINE_KEY = "baselinePercentage";
const TESTED_KEY = "testedPercentage";

const BASELINE_LABEL = "Original (published)";
const TESTED_LABEL = "This test";

const BASELINE_COLOR = "var(--color-gray-300)";
const TESTED_COLOR = "var(--color-violet-400)";

interface CategoryDatum {
  key: string;
  label: string;
  baselineCount: number;
  testedCount: number;
  baselinePercentage: number;
  testedPercentage: number;
}

interface SectionApplicabilityComparisonChartProps {
  /** Omitted/null for `new_section` proposals — chart renders tested only. */
  readonly baseline?: PlaybookNodeSampleToolResultSectionSideAggregates | null;
  readonly tested: PlaybookNodeSampleToolResultSectionSideAggregates;
}

export const SectionApplicabilityComparisonChart: FC<
  SectionApplicabilityComparisonChartProps
> = ({ baseline, tested }) => {
  const hasBaseline = baseline != null;

  const data = useMemo(
    () => buildCategories(baseline ?? null, tested),
    [baseline, tested],
  );

  const hasData = data.some((d) => d.baselineCount > 0 || d.testedCount > 0);
  if (!hasData) return null;

  return (
    <div className="space-y-2">
      <UiLabel size={Size.SMALL}>Call volume by applicability</UiLabel>

      <div className="flex items-center gap-4 text-xs text-gray-600">
        {hasBaseline ? (
          <span className="inline-flex items-center gap-1.5">
            <LegendSwatch color={BASELINE_COLOR} />
            <span>{BASELINE_LABEL}</span>
          </span>
        ) : null}
        <span className="inline-flex items-center gap-1.5">
          <LegendSwatch color={TESTED_COLOR} />
          <span>{TESTED_LABEL}</span>
        </span>
      </div>

      <div style={{ width: "100%", height: 180 }}>
        <ResponsiveContainer>
          <BarChart
            data={data}
            margin={{ top: 10, right: 10, bottom: 24, left: 0 }}
            barCategoryGap="18%"
          >
            <CartesianGrid
              vertical={false}
              stroke="var(--color-gray-200)"
              strokeDasharray="3 3"
            />
            <XAxis
              dataKey="label"
              stroke="none"
              tickLine={false}
              tick={{ fontSize: 10, fill: "var(--color-gray-500)" }}
              interval={0}
            >
              <ChartLabel
                value="Section applicability"
                position="insideBottom"
                offset={-12}
                style={{
                  fontSize: 10,
                  fill: "var(--color-gray-500)",
                  fontWeight: 500,
                }}
              />
            </XAxis>
            <YAxis
              stroke="none"
              tickLine={false}
              tickFormatter={(value: number) => `${Math.round(value)}%`}
              tick={{ fontSize: 10, fill: "var(--color-gray-500)" }}
              width={32}
              domain={[0, (max: number) => Math.max(Math.ceil(max), 1)]}
            />
            <Tooltip
              cursor={{ fill: "var(--color-gray-50)" }}
              content={(tooltipProps) => (
                <ChartTooltip {...tooltipProps} hasBaseline={hasBaseline} />
              )}
            />
            {hasBaseline ? (
              <Bar
                dataKey={BASELINE_KEY}
                name={BASELINE_LABEL}
                fill={BASELINE_COLOR}
                stroke="var(--color-gray-400)"
                radius={2}
              />
            ) : null}
            <Bar
              dataKey={TESTED_KEY}
              name={TESTED_LABEL}
              fill={TESTED_COLOR}
              stroke="var(--color-violet-500)"
              radius={2}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const LegendSwatch: FC<{ color: string }> = ({ color }) => (
  <span
    className="inline-block size-2.5 rounded-xs"
    style={{ backgroundColor: color }}
  />
);

const ChartTooltip: FC<TooltipContentProps & { hasBaseline: boolean }> = ({
  active,
  payload,
  label,
  hasBaseline,
}) => {
  if (!active || !payload?.length) return null;
  const datum = payload[0]?.payload as CategoryDatum | undefined;
  if (!datum) return null;

  return (
    <div className="rounded-sm border border-border-light bg-white px-3 py-2 text-sm shadow-xs">
      <div className="mb-1 font-medium text-foreground">{label}</div>
      {hasBaseline ? (
        <TooltipRow
          color={BASELINE_COLOR}
          label={BASELINE_LABEL}
          percentage={datum.baselinePercentage}
          count={datum.baselineCount}
        />
      ) : null}
      <TooltipRow
        color={TESTED_COLOR}
        label={TESTED_LABEL}
        percentage={datum.testedPercentage}
        count={datum.testedCount}
      />
    </div>
  );
};

const TooltipRow: FC<{
  color: string;
  label: string;
  percentage: number;
  count: number;
}> = ({ color, label, percentage, count }) => (
  <div className="flex items-center gap-2">
    <span
      className="inline-block h-2.5 w-2.5 rounded-xs"
      style={{ backgroundColor: color }}
    />
    <span className="text-gray-600">{label}</span>
    <span className="ml-auto font-medium">
      {Math.round(percentage)}% · {count} call{count === 1 ? "" : "s"}
    </span>
  </div>
);

function buildCategories(
  baseline: PlaybookNodeSampleToolResultSectionSideAggregates | null,
  tested: PlaybookNodeSampleToolResultSectionSideAggregates,
): CategoryDatum[] {
  const baselineTotal = baseline ? computeSideTotal(baseline) : 0;
  const testedTotal = computeSideTotal(tested);

  return CATEGORY_KEYS.map(({ key, label }) => {
    const baselineCount = baseline?.score_distribution[key] ?? 0;
    const testedCount = tested.score_distribution[key] ?? 0;
    return {
      key,
      label,
      baselineCount,
      testedCount,
      baselinePercentage: toPercentage(baselineCount, baselineTotal),
      testedPercentage: toPercentage(testedCount, testedTotal),
    };
  });
}

function computeSideTotal(
  side: PlaybookNodeSampleToolResultSectionSideAggregates,
): number {
  let total = 0;
  for (const count of Object.values(side.score_distribution)) {
    total += count ?? 0;
  }
  return total;
}

function toPercentage(count: number, total: number): number {
  if (total <= 0) return 0;
  return (count / total) * 100;
}
